import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, Download, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Required and optional columns for CSV import
const CSV_COLUMNS = {
  required: [
    { name: 'name', description: 'Customer name (required)', example: 'John Smith' }
  ],
  optional: [
    { name: 'address', description: 'Full address', example: '123 High Street, Brighton, BN1 1AA' },
    { name: 'email', description: 'Email address', example: 'john@example.com' },
    { name: 'phone', description: 'Phone number', example: '07123456789' },
    { name: 'zone', description: 'Round/Zone name', example: 'Monday Round' },
    { name: 'round', description: 'Round name (alias for zone)', example: 'Monday Round' },
    { name: 'notes', description: 'Customer notes', example: 'Key under mat' },
    { name: 'tags', description: 'Tags (comma separated)', example: 'regular,priority' },
    // Job/Service related fields
    { name: 'services', description: 'Service type', example: 'Window Clean' },
    { name: 'service', description: 'Service type (alias)', example: 'Window Clean' },
    { name: 'title', description: 'Job title/service', example: 'Window Clean Every 6 weeks' },
    { name: 'price', description: 'Job price', example: '25.00' },
    { name: 'frequency', description: 'Job frequency', example: 'Every 6 weeks' },
    { name: 'duration', description: 'Job duration', example: '00:30' },
    { name: 'next_due', description: 'Next due date', example: '2026-03-15' },
    { name: 'last_done', description: 'Last completed date', example: '2026-02-01' },
    { name: 'assignee', description: 'Assigned worker', example: 'Steve' }
  ]
};

const CSVImport = ({ onClose, onImportComplete, isSyncCentre = false }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [importStatus, setImportStatus] = useState(null); // 'parsing', 'ready', 'importing', 'complete', 'error'
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [newCustomers, setNewCustomers] = useState([]);
  const [showMigrationWarning, setShowMigrationWarning] = useState(!isSyncCentre);
  // Squeegee export mode — full Squeegee CSVs (one row per job, customer
  // fields repeated) are handled entirely server-side.
  const [squeegeeMode, setSqueegeeMode] = useState(false);
  const [squeegeePreview, setSqueegeePreview] = useState(null);

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    // Parse header row
    const headerRow = lines[0];
    const csvHeaders = headerRow.split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
    
    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length > 0) {
        const row = {};
        csvHeaders.forEach((header, idx) => {
          row[header] = values[idx]?.trim() || '';
        });
        data.push(row);
      }
    }

    return { headers: csvHeaders, data };
  };

  // Parse a single CSV line (handles quoted values with commas)
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    return values.map(v => v.replace(/^["']|["']$/g, ''));
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setImportStatus('parsing');

    try {
      const text = await selectedFile.text();

      // Squeegee export? Hand the raw file to the backend — the browser
      // parser can't handle Squeegee's multi-line quoted addresses.
      if (text.slice(0, 2000).includes('Job - Job Ref')) {
        setSqueegeeMode(true);
        const token = localStorage.getItem('streakfree_token');
        const fd = new FormData();
        fd.append('file', selectedFile);
        const res = await fetch(`${API_URL}/api/import/squeegee/preview`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || 'Failed to analyse Squeegee file');
        }
        setSqueegeePreview(await res.json());
        setImportStatus('ready');
        return;
      }
      setSqueegeeMode(false);
      setSqueegeePreview(null);
      const { headers: csvHeaders, data } = parseCSV(text);
      
      setHeaders(csvHeaders);
      setParsedData(data);

      // Auto-map columns based on header names with stricter matching
      const mapping = {};
      const allColumns = [...CSV_COLUMNS.required, ...CSV_COLUMNS.optional];
      
      // Define exact matches and aliases for better mapping
      const columnAliases = {
        'name': ['name', 'customer', 'customer_name', 'customername', 'full_name', 'fullname', 'client'],
        'address': ['address', 'full_address', 'fulladdress', 'street_address', 'streetaddress', 'location'],
        'email': ['email', 'email_address', 'emailaddress', 'e-mail', 'e_mail'],
        'phone': ['phone', 'telephone', 'tel', 'mobile', 'cell', 'phone_number', 'phonenumber', 'contact_number'],
        'zone': ['zone', 'area', 'region', 'territory'],
        'round': ['round', 'rounds', 'route', 'routes'],
        'notes': ['notes', 'note', 'comments', 'comment'],
        'postcode': ['postcode', 'post_code', 'zip', 'zipcode', 'zip_code', 'postal'],
        'town': ['town', 'city', 'suburb'],
        'street': ['street', 'street_name', 'streetname', 'road'],
        'tags': ['tags', 'tag', 'labels', 'label', 'categories'],
        'services': ['services', 'service_types', 'job_types'],
        'service': ['service', 'service_type', 'servicetype', 'job_type', 'jobtype'],
        'title': ['title', 'job_title', 'jobtitle'],
        'price': ['price', 'amount', 'cost', 'fee', 'charge', 'rate'],
        'frequency': ['frequency', 'freq', 'schedule', 'interval', 'recurring'],
        'duration': ['duration', 'time', 'estimated_time', 'job_time'],
        'next_due': ['next_due', 'nextdue', 'next_date', 'nextdate', 'due_date', 'duedate', 'scheduled'],
        'last_done': ['last_done', 'lastdone', 'last_date', 'lastdate', 'completed'],
        'assignee': ['assignee', 'assigned', 'worker', 'staff', 'employee']
      };
      
      csvHeaders.forEach(header => {
        const normalizedHeader = header.toLowerCase().replace(/[_\s-]/g, '').replace(/['"]/g, '');
        
        for (const col of allColumns) {
          const aliases = columnAliases[col.name] || [col.name];
          const matched = aliases.some(alias => {
            const normalizedAlias = alias.toLowerCase().replace(/[_\s-]/g, '');
            return normalizedHeader === normalizedAlias;
          });
          
          if (matched && !mapping[col.name]) {
            mapping[col.name] = header;
            break;
          }
        }
      });

      setColumnMapping(mapping);
      
      // Check for duplicates against existing customers
      await checkDuplicates(data, mapping);
      
      setImportStatus('ready');
    } catch (err) {
      setError(err.message);
      setImportStatus('error');
    }
  };

  // Check for duplicates in database
  const checkDuplicates = async (data, mapping) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers?limit=5000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const existingData = await response.json();
        const existingCustomers = Array.isArray(existingData) ? existingData : (existingData.customers || []);
        
        // Create a set of existing customer identifiers (name + address normalized)
        const existingSet = new Set();
        existingCustomers.forEach(c => {
          const key = `${(c.name || '').toLowerCase().trim()}|${(c.address || '').toLowerCase().trim()}`;
          existingSet.add(key);
        });

        // Check each row for duplicates
        const dupes = [];
        const newOnes = [];
        
        data.forEach((row, idx) => {
          const nameCol = mapping.name || 'name';
          const addressCol = mapping.address || 'address';
          const name = (row[nameCol] || '').toLowerCase().trim();
          const address = (row[addressCol] || '').toLowerCase().trim();
          const key = `${name}|${address}`;
          
          if (existingSet.has(key)) {
            dupes.push({ ...row, _rowIndex: idx });
          } else {
            newOnes.push({ ...row, _rowIndex: idx });
          }
        });

        setDuplicates(dupes);
        setNewCustomers(newOnes);
      }
    } catch (err) {
      console.error('Error checking duplicates:', err);
    }
  };

  // Handle column mapping change
  const handleMappingChange = (targetColumn, sourceColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [targetColumn]: sourceColumn
    }));
  };

  // Import customers
  const handleImport = async () => {
    // Squeegee mode — backend does everything (grouping, jobs, geocoding)
    if (squeegeeMode) {
      setImportStatus('importing');
      setError(null);
      try {
        const token = localStorage.getItem('streakfree_token');
        const fd = new FormData();
        fd.append('file', file);
        const response = await fetch(`${API_URL}/api/import/squeegee`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        if (!response.ok) {
          const d = await response.json().catch(() => ({}));
          throw new Error(d.detail || 'Squeegee import failed');
        }
        const result = await response.json();
        setImportResult({
          created: result.created_customers,
          jobsCreated: result.created_jobs,
          skipped: result.skipped_customers,
          errors: result.error_count || 0,
          errorDetails: (result.errors || []).map((e, i) => ({ index: i, error: `${e.customer}: ${e.error}` })),
          geocoding: result.geocoding
        });
        setImportStatus('complete');
        if (onImportComplete) onImportComplete(result.created_customers);
      } catch (err) {
        setError(err.message);
        setImportStatus('error');
      }
      return;
    }

    if (!columnMapping.name) {
      setError('Please map the "name" column - it is required');
      return;
    }

    setImportStatus('importing');
    setError(null);

    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Transform data using column mapping - only new customers
      const customers = newCustomers.map(row => {
        const customer = {
          name: row[columnMapping.name] || ''
        };

        if (columnMapping.address) customer.address = row[columnMapping.address];
        if (columnMapping.email) customer.email = row[columnMapping.email];
        if (columnMapping.phone) customer.phone = row[columnMapping.phone];
        if (columnMapping.zone) customer.zone = row[columnMapping.zone];
        if (columnMapping.round) customer.round = row[columnMapping.round];
        if (columnMapping.notes) customer.notes = row[columnMapping.notes];
        if (columnMapping.tags) {
          const tagsStr = row[columnMapping.tags] || '';
          customer.tags = tagsStr.split(',').map(t => t.trim()).filter(t => t);
        }
        
        // Job/Service related fields
        if (columnMapping.services) customer.services = row[columnMapping.services];
        if (columnMapping.service) customer.service = row[columnMapping.service];
        if (columnMapping.title) customer.title = row[columnMapping.title];
        if (columnMapping.price) {
          const priceStr = (row[columnMapping.price] || '').replace('£', '').replace(',', '').trim();
          customer.price = parseFloat(priceStr) || null;
        }
        if (columnMapping.frequency) customer.frequency = row[columnMapping.frequency];
        if (columnMapping.duration) customer.duration = row[columnMapping.duration];
        if (columnMapping.next_due) customer.next_due = row[columnMapping.next_due];
        if (columnMapping.last_done) customer.last_done = row[columnMapping.last_done];
        if (columnMapping.assignee) customer.assignee = row[columnMapping.assignee];

        return customer;
      }).filter(c => c.name); // Only include rows with a name

      if (customers.length === 0) {
        setError('No valid customers to import');
        setImportStatus('ready');
        return;
      }

      // Import in batches of 100
      const batchSize = 100;
      let totalCreated = 0;
      let totalJobsCreated = 0;
      let totalErrors = 0;
      const allErrors = [];
      const allServicesFound = new Set();

      for (let i = 0; i < customers.length; i += batchSize) {
        const batch = customers.slice(i, i + batchSize);
        
        const response = await fetch(`${API_URL}/api/customers/bulk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ customers: batch, include_jobs: true })
        });

        if (response.ok) {
          const result = await response.json();
          totalCreated += result.created_count || 0;
          totalJobsCreated += result.jobs_created || 0;
          totalErrors += result.error_count || 0;
          if (result.errors) allErrors.push(...result.errors);
          if (result.services_found) {
            result.services_found.forEach(s => allServicesFound.add(s));
          }
        } else {
          totalErrors += batch.length;
        }
      }

      setImportResult({
        created: totalCreated,
        jobsCreated: totalJobsCreated,
        servicesFound: Array.from(allServicesFound),
        skipped: duplicates.length,
        errors: totalErrors,
        errorDetails: allErrors
      });
      setImportStatus('complete');

      if (onImportComplete) {
        onImportComplete(totalCreated);
      }
    } catch (err) {
      setError(err.message);
      setImportStatus('error');
    }
  };

  // Download sample CSV
  const downloadSampleCSV = () => {
    const headers = ['name', 'address', 'email', 'phone', 'zone', 'notes', 'tags'];
    const sampleData = [
      'John Smith,"123 High Street, Brighton, BN1 1AA",john@email.com,07123456789,Monday Round,Key under mat,"regular,priority"',
      'Jane Doe,"45 Oak Avenue, Hove, BN3 2AB",jane@email.com,07987654321,Tuesday Round,Ring bell twice,regular'
    ];
    
    const csv = [headers.join(','), ...sampleData].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'streakfree_customer_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full modal-shell-h overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-zinc-900">Import Customers from CSV</h2>
            <p className="text-sm text-zinc-500 mt-1">Upload a CSV file to add customers in bulk</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Migration Warning - Show when not accessed from Sync Centre */}
          {showMigrationWarning && !file && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800">Migrating from another CRM?</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    If you're importing customers from Squeegee or another CRM with job data, 
                    please use <strong>Sync Centre → Squeegee/CRM Import</strong> instead. 
                    This ensures your jobs and schedules are properly linked.
                  </p>
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => {
                        onClose();
                        window.location.href = '/sync-centre';
                      }}
                      className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
                    >
                      Go to Sync Centre
                    </button>
                    <button
                      onClick={() => setShowMigrationWarning(false)}
                      className="text-sm text-amber-700 hover:text-amber-800"
                    >
                      I understand, continue here
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Instructions */}
          {!file && (
            <div className="mb-6">
              <h3 className="font-medium text-zinc-900 mb-3">CSV Format Requirements</h3>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <h4 className="font-medium text-emerald-800 mb-2">Required Column</h4>
                  {CSV_COLUMNS.required.map(col => (
                    <div key={col.name} className="text-sm">
                      <span className="font-mono text-emerald-700">{col.name}</span>
                      <span className="text-emerald-600 ml-2">- {col.description}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-zinc-50 rounded-xl p-4">
                  <h4 className="font-medium text-zinc-700 mb-2">Optional Columns</h4>
                  {CSV_COLUMNS.optional.map(col => (
                    <div key={col.name} className="text-sm text-zinc-600">
                      <span className="font-mono">{col.name}</span>
                      <span className="ml-2">- {col.description}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={downloadSampleCSV}
                className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700"
              >
                <Download size={16} />
                Download sample CSV template
              </button>
            </div>
          )}

          {/* File Upload */}
          {!file && (
            <div className="border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center hover:border-emerald-300 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload size={48} className="mx-auto text-zinc-300 mb-4" />
                <p className="text-zinc-600 font-medium">Drop your CSV file here or click to browse</p>
                <p className="text-sm text-zinc-400 mt-1">Only .csv files are supported</p>
              </label>
            </div>
          )}

          {/* Parsing Status */}
          {importStatus === 'parsing' && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              <span className="ml-3 text-zinc-600">Parsing CSV file...</span>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 text-red-700 rounded-xl p-4 flex items-start gap-3 mb-4">
              <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Preview & Mapping */}
          {importStatus === 'ready' && squeegeeMode && squeegeePreview && (
            <div data-testid="squeegee-preview-panel">
              <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <h4 className="font-semibold text-emerald-800">Squeegee export detected ✓</h4>
                <p className="text-sm text-emerald-700 mt-1">
                  Customers, jobs, prices, frequencies, due dates, rounds and assignees will all be imported. Map pins are added automatically after import.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-zinc-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-semibold text-zinc-900">{squeegeePreview.customers}</p>
                  <p className="text-sm text-zinc-500">Customers</p>
                </div>
                <div className="bg-zinc-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-semibold text-zinc-900">{squeegeePreview.jobs}</p>
                  <p className="text-sm text-zinc-500">Jobs</p>
                </div>
                <div className="bg-zinc-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-semibold text-zinc-900">{squeegeePreview.jobs_without_due_date}</p>
                  <p className="text-sm text-zinc-500">Ad hoc (no due date)</p>
                </div>
              </div>
              {squeegeePreview.multi_job_customers?.length > 0 && (
                <div className="mb-4 bg-sky-50 border border-sky-200 rounded-xl p-4">
                  <h4 className="font-semibold text-sky-800 text-sm mb-1">Customers with multiple jobs</h4>
                  {squeegeePreview.multi_job_customers.map((m, i) => (
                    <p key={i} className="text-sm text-sky-700">{m.name} — {m.jobs} jobs: {m.addresses.join(' • ')}</p>
                  ))}
                </div>
              )}
              {squeegeePreview.customers_with_balance?.length > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h4 className="font-semibold text-amber-800 text-sm mb-1">Balances carried over</h4>
                  {squeegeePreview.customers_with_balance.map((b, i) => (
                    <p key={i} className="text-sm text-amber-700">{b.name} — £{b.balance.toFixed(2)}</p>
                  ))}
                </div>
              )}
              {squeegeePreview.existing_customers > 0 && (
                <p className="text-sm text-zinc-500">You already have {squeegeePreview.existing_customers} customers — exact name+address matches will be skipped.</p>
              )}
            </div>
          )}

          {importStatus === 'ready' && !squeegeeMode && (
            <>
              {/* Duplicate Warning - Block import if most records are duplicates */}
              {duplicates.length > 0 && duplicates.length >= parsedData.length * 0.5 && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h4 className="font-semibold text-red-800">Most customers already exist!</h4>
                      <p className="text-sm text-red-700 mt-1">
                        {duplicates.length} of {parsedData.length} customers in this file already exist in your account. 
                        This usually means you've already imported this file.
                      </p>
                      <p className="text-sm text-red-600 mt-2">
                        Only {newCustomers.length} new customer{newCustomers.length !== 1 ? 's' : ''} would be imported.
                        Please check your existing customers before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-zinc-50 rounded-xl p-4 text-center">
                  <FileText className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
                  <p className="text-2xl font-semibold text-zinc-900">{parsedData.length}</p>
                  <p className="text-sm text-zinc-500">Rows in CSV</p>
                </div>
                
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-2xl font-semibold text-emerald-700">{newCustomers.length}</p>
                  <p className="text-sm text-emerald-600">New to import</p>
                </div>
                
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <AlertCircle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-2xl font-semibold text-amber-700">{duplicates.length}</p>
                  <p className="text-sm text-amber-600">Duplicates (skipped)</p>
                </div>
              </div>

              {/* Column Mapping */}
              <div className="mb-6">
                <h3 className="font-medium text-zinc-900 mb-3">Column Mapping</h3>
                <p className="text-sm text-zinc-500 mb-4">Match your CSV columns to StreakFree fields</p>
                
                <div className="grid md:grid-cols-2 gap-3">
                  {[...CSV_COLUMNS.required, ...CSV_COLUMNS.optional].map(col => (
                    <div key={col.name} className="flex items-center gap-3">
                      <label className="w-24 text-sm font-medium text-zinc-700">
                        {col.name}
                        {CSV_COLUMNS.required.find(r => r.name === col.name) && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      <select
                        value={columnMapping[col.name] || ''}
                        onChange={(e) => handleMappingChange(col.name, e.target.value)}
                        className="flex-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="">-- Not mapped --</option>
                        {headers.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Table */}
              <div className="mb-6">
                <h3 className="font-medium text-zinc-900 mb-3">Preview (first 5 rows to import)</h3>
                <div className="overflow-x-auto border border-zinc-200 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-zinc-600">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-zinc-600">Address</th>
                        <th className="px-4 py-2 text-left font-medium text-zinc-600">Phone</th>
                        <th className="px-4 py-2 text-left font-medium text-zinc-600">Zone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newCustomers.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="border-t border-zinc-100">
                          <td className="px-4 py-2 text-zinc-900">{row[columnMapping.name] || '-'}</td>
                          <td className="px-4 py-2 text-zinc-600">{row[columnMapping.address] || '-'}</td>
                          <td className="px-4 py-2 text-zinc-600">{row[columnMapping.phone] || '-'}</td>
                          <td className="px-4 py-2 text-zinc-600">{row[columnMapping.zone] || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Importing Status */}
          {importStatus === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mb-4" />
              <p className="text-lg font-medium text-zinc-900">Importing customers...</p>
              <p className="text-sm text-zinc-500">This may take a moment</p>
            </div>
          )}

          {/* Import Complete */}
          {importStatus === 'complete' && importResult && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">Import Complete!</h3>
              
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mt-6">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-2xl font-semibold text-emerald-700">{importResult.created}</p>
                  <p className="text-sm text-emerald-600">Added</p>
                </div>
                <div className="bg-sky-50 rounded-xl p-4">
                  <p className="text-2xl font-semibold text-sky-700">{importResult.jobsCreated || 0}</p>
                  <p className="text-sm text-sky-600">Jobs</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-2xl font-semibold text-amber-700">{importResult.skipped}</p>
                  <p className="text-sm text-amber-600">Skipped</p>
                </div>
              </div>
              {importResult.geocoding && (
                <p className="text-sm text-zinc-500 mt-4">📍 {importResult.geocoding}</p>
              )}
              {importResult.errors > 0 && (
                <p className="text-sm text-red-600 mt-2">{importResult.errors} errors</p>
              )}

              {importResult.errorDetails?.length > 0 && (
                <div className="mt-6 text-left max-w-md mx-auto">
                  <p className="text-sm font-medium text-red-700 mb-2">Error details:</p>
                  <div className="bg-red-50 rounded-lg p-3 text-sm text-red-600 max-h-32 overflow-y-auto">
                    {importResult.errorDetails.map((err, idx) => (
                      <div key={idx}>Row {err.index + 1}: {err.error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-100 flex justify-between items-center">
          {importStatus === 'ready' && (
            <>
              <button
                onClick={() => {
                  setFile(null);
                  setParsedData([]);
                  setImportStatus(null);
                }}
                className="px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                Choose different file
              </button>
              
              <button
                onClick={handleImport}
                disabled={!squeegeeMode && newCustomers.length === 0}
                data-testid="csv-import-confirm-btn"
                className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload size={18} />
                {squeegeeMode && squeegeePreview
                  ? `Import ${squeegeePreview.customers} Customers & ${squeegeePreview.jobs} Jobs`
                  : `Import ${newCustomers.length} Customers`}
              </button>
            </>
          )}

          {importStatus === 'complete' && (
            <button
              onClick={onClose}
              className="ml-auto px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
            >
              Done
            </button>
          )}

          {!importStatus && (
            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVImport;
